import { useState } from 'react';

import {
    type QueryObserverResult,
    type RefetchOptions,
    type RefetchQueryFilters,
    useQuery
} from '@tanstack/react-query';
import axios from 'axios';

import { useUserUuid } from './useUserUuid';
import { GET_MERGED_CLASS_SETTINGS } from '../config/apiPaths';
import { type SchoolSettings } from '../types/apiTypes';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { processApiPath } from '../utils/url';

interface MergedClassAndAdminSettings {
    mergedClassAndAdminSettings: SchoolSettings | null | undefined;
    mergedClassAndAdminSettingsFetched: boolean;
    mergedClassAndAdminSettingsError: boolean;
    mergedClassAndAdminSettingsErrorCode: number | null;
    refetchMergedClassAndAdminSettings: (
        options?: RefetchOptions & RefetchQueryFilters
    ) => Promise<QueryObserverResult<SchoolSettings | null, unknown>>;
}

const useMergedClassSettingsQuery = (classUuid: string): MergedClassAndAdminSettings => {
    const userUuid = useUserUuid(null) || '';
    const [mergedClassAndAdminErrorCode, setMergedClassAndAdminErrorCode] = useState<number | null>(null);

    const {
        data: mergedClassAndAdminSettings,
        isFetched: mergedClassAndAdminSettingsFetched,
        isError: mergedClassAndAdminSettingsError,
        refetch: refetchMergedClassAndAdminSettings
    } = useQuery({
        queryKey: [GET_MERGED_CLASS_SETTINGS, classUuid],
        enabled: !!classUuid && !!userUuid,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            if (!classUuid) {
                return null;
            }
            try {
                const { data } = await axios.get<SchoolSettings>(processApiPath(GET_MERGED_CLASS_SETTINGS), {
                    headers: getUserUuidHeaders(userUuid),
                    params: {
                        class_uuid: classUuid
                    }
                });
                return data as SchoolSettings;
            } catch (error) {
                setMergedClassAndAdminErrorCode((error as any).response?.status);
                throw error;
            }
        }
    });

    return {
        mergedClassAndAdminSettings,
        mergedClassAndAdminSettingsFetched: mergedClassAndAdminSettingsFetched,
        mergedClassAndAdminSettingsError,
        mergedClassAndAdminSettingsErrorCode: mergedClassAndAdminErrorCode,
        refetchMergedClassAndAdminSettings
    };
};

export default useMergedClassSettingsQuery;
